<template>
  <v-form ref="form" v-model="valid">
    <v-row dense>
      <v-col cols="12" md="6">
        <v-text-field
          v-model="formData.name"
          outlined
          dense
          placeholder="VD: Máy chiếu Panasonic, Bút bi Thiên Long..."
          :rules="[v => !!v || 'Vui lòng nhập tên thiết bị']"
          @input="onNameInput"
        >
          <template v-slot:label>
            Tên thiết bị <span class="red--text font-weight-bold">*</span>
          </template>
        </v-text-field>
      </v-col>
      <v-col cols="12" md="6">
        <v-autocomplete
          v-model="formData.category"
          :items="categories"
          item-text="name"
          item-value="id"
          outlined
          dense
          clearable
          placeholder="Chọn đầu mục thiết bị"
          :rules="[v => !!v || 'Vui lòng chọn đầu mục']"
          @change="onCategoryChange"
        >
          <template v-slot:label>
            Đầu mục thiết bị <span class="red--text font-weight-bold">*</span>
          </template>
          <template v-slot:item="{ item }">
            <div :style="{ paddingLeft: (item.depth || 0) * 14 + 'px' }" class="d-flex align-center w-100">
              <v-icon small :color="item.depth === 0 ? 'primary' : 'primary lighten-1'" class="mr-2">
                {{ item.depth === 0 ? 'mdi-folder' : 'mdi-folder-outline' }}
              </v-icon>
              <span :class="item.depth === 0 ? 'font-weight-bold text-body-2' : 'text-body-2'">
                {{ item.name }}
              </span>
              <span v-if="item.code" class="grey--text ml-2 text-caption">({{ item.code }})</span>
            </div>
          </template>
        </v-autocomplete>
      </v-col>
      <v-col cols="12" md="4">
        <v-text-field
          v-model="formData.code"
          outlined
          dense
          placeholder="VD: MC_C_001 (Tự sinh theo đầu mục & đơn vị)"
          @input="onCodeManualInput"
        >
          <template v-slot:label>
            Mã thiết bị / Barcode <span class="red--text font-weight-bold">*</span>
          </template>
          <template v-slot:append>
            <v-btn x-small text color="primary" @click="getAutoCode" title="Tự sinh mã theo đầu mục & đơn vị">Tự sinh</v-btn>
          </template>
        </v-text-field>
      </v-col>
      <v-col cols="12" md="4">
        <v-autocomplete
          v-model="formData.location"
          :items="locations"
          item-text="name"
          item-value="id"
          outlined
          dense
          clearable
          placeholder="Chọn vị trí lưu trữ"
          :rules="[v => !!v || 'Vui lòng chọn vị trí']"
        >
          <template v-slot:label>
            Vị trí lưu trữ / Phòng <span class="red--text font-weight-bold">*</span>
          </template>
          <template v-slot:item="{ item }">
            <div :style="{ paddingLeft: (item.depth || 0) * 14 + 'px' }" class="d-flex align-center w-100">
              <v-icon small :color="item.type | locationColor" class="mr-2">
                {{ item.type | locationIcon }}
              </v-icon>
              <span :class="item.depth === 0 ? 'font-weight-bold text-body-2' : 'text-body-2'">
                {{ item.name }}
              </span>
              <span v-if="item.code" class="grey--text ml-2 text-caption">({{ item.code }})</span>
            </div>
          </template>
        </v-autocomplete>
      </v-col>
      <v-col cols="12" md="4">
        <v-select
          v-model="formData.academicLevel"
          :items="academicLevels"
          item-text="title"
          item-value="id"
          label="Cấp học áp dụng"
          outlined
          dense
          clearable
        />
      </v-col>
      <v-col cols="12" md="4">
        <v-text-field
          v-model.number="formData.quantity"
          type="number"
          min="0"
          outlined
          dense
          placeholder="Nhập số lượng (VD: 0, 10, 50...)"
          :rules="[v => v >= 0 || 'Số lượng không được âm']"
        >
          <template v-slot:label>
            Tổng số lượng <span class="red--text font-weight-bold">*</span>
          </template>
        </v-text-field>
      </v-col>
      <v-col cols="12" md="4">
        <!-- Người dùng có thể chọn gợi ý hoặc tự do gõ bất kỳ đơn vị nào -->
        <v-combobox
          v-model="formData.unit"
          :items="suggestedUnits"
          outlined
          dense
          placeholder="Chọn hoặc tự gõ (Cái, Hộp, Bộ...)"
          :rules="[v => !!v || 'Vui lòng nhập đơn vị tính']"
          @change="onUnitChange"
        >
          <template v-slot:label>
            Đơn vị tính <span class="red--text font-weight-bold">*</span>
          </template>
        </v-combobox>
      </v-col>
      <v-col cols="12" md="4">
        <v-select
          v-model="formData.status"
          :items="statusList"
          outlined
          dense
        >
          <template v-slot:label>
            Trạng thái <span class="red--text font-weight-bold">*</span>
          </template>
        </v-select>
      </v-col>

      <!-- Khối quy đổi đơn vị tùy chọn -->
      <v-col cols="12">
        <v-card outlined class="pa-3 grey lighten-5">
          <div class="d-flex justify-space-between align-center mb-2">
            <span class="text-caption font-weight-bold text-uppercase primary--text">
              Quy đổi đơn vị tính (Tùy chọn)
            </span>
            <v-btn x-small text color="primary" @click="addUnitConversion">
              <v-icon x-small left>mdi-plus</v-icon> Thêm đơn vị đóng gói
            </v-btn>
          </div>

          <div v-if="formData.unitConversions.length === 0" class="text-caption grey--text text--darken-1">
            Không có quy đổi (Sử dụng 1 đơn vị: {{ formData.unit || 'chưa đặt' }})
          </div>

          <v-row v-for="(conv, index) in formData.unitConversions" :key="index" dense align="center">
            <v-col cols="12" md="5">
              <v-text-field
                v-model="conv.unitName"
                label="Tên đơn vị lớn hơn"
                placeholder="VD: Hộp, Thùng, Ram..."
                outlined
                dense
                hide-details
              />
            </v-col>
            <v-col cols="12" md="5">
              <v-text-field
                v-model.number="conv.conversionRate"
                type="number"
                min="1"
                :label="`1 ${conv.unitName || 'đơn vị'} = ? ${formData.unit || 'đơn vị'}`"
                outlined
                dense
                hide-details
              />
            </v-col>
            <v-col cols="12" md="2" class="d-flex justify-center">
              <v-btn icon small color="error" @click="removeUnitConversion(index)">
                <v-icon small>mdi-delete</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-card>
      </v-col>

      <v-col cols="12" md="6">
        <v-text-field
          v-model="formData.purchaseDate"
          type="date"
          label="Ngày mua sắm"
          outlined
          dense
        />
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field
          v-model="formData.warrantyExpiry"
          type="date"
          label="Hạn bảo hành"
          outlined
          dense
        />
      </v-col>
      <v-col cols="12">
        <v-textarea
          v-model="formData.specifications"
          label="Thông số kỹ thuật / Cấu hình"
          outlined
          dense
          rows="2"
          placeholder="RAM, CPU, Độ phân giải, Model chi tiết..."
        />
      </v-col>
      <v-col cols="12">
        <v-textarea
          v-model="formData.note"
          label="Ghi chú thêm"
          outlined
          dense
          rows="2"
        />
      </v-col>
    </v-row>
  </v-form>
</template>

<script>
import { AcademicLevel, AssetCategory, FacilityLocation, Asset } from '@/plugins/api'

export default {
  name: 'AssetForm',
  props: {
    assetData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      valid: true,
      categories: [],
      locations: [],
      academicLevels: [],
      suggestedUnits: ['Cái', 'Chiếc', 'Bộ', 'Hộp', 'Thùng', 'Lít', 'Kg', 'Ram', 'Cuộn', 'Mét', 'Bình', 'Chai'],
      statusList: [
        { text: 'Khả dụng', value: 'AVAILABLE' },
        { text: 'Đang mượn', value: 'BORROWED' },
        { text: 'Đang bảo trì', value: 'MAINTENANCE' },
        { text: 'Bị hỏng', value: 'BROKEN' }
      ],
      isManualCode: false,
      timerCode: null,
      formData: {
        id: null,
        code: '',
        name: '',
        category: null,
        location: null,
        academicLevel: null,
        quantity: 0,
        unit: '',
        unitConversions: [],
        status: 'AVAILABLE',
        purchaseDate: null,
        warrantyExpiry: null,
        specifications: '',
        note: ''
      }
    }
  },
  watch: {
    assetData: {
      immediate: true,
      handler(val) {
        if (val && val.id) {
          this.isManualCode = true
          this.formData = {
            id: val.id,
            code: val.code || '',
            name: val.name || '',
            category: (val.category && val.category.id) || val.category || null,
            location: (val.location && val.location.id) || val.location || null,
            academicLevel: (val.academicLevel && val.academicLevel.id) || val.academicLevel || null,
            quantity: val.quantity !== undefined ? val.quantity : 0,
            unit: val.unit || '',
            unitConversions: Array.isArray(val.unitConversions) ? [...val.unitConversions] : [],
            status: val.status || 'AVAILABLE',
            purchaseDate: val.purchaseDate ? val.purchaseDate.substring(0, 10) : null,
            warrantyExpiry: val.warrantyExpiry ? val.warrantyExpiry.substring(0, 10) : null,
            specifications: val.specifications || '',
            note: val.note || ''
          }
        } else {
          this.isManualCode = false
          this.formData = {
            id: null,
            code: '',
            name: '',
            category: null,
            location: null,
            academicLevel: null,
            quantity: 0,
            unit: '',
            unitConversions: [],
            status: 'AVAILABLE',
            purchaseDate: null,
            warrantyExpiry: null,
            specifications: '',
            note: ''
          }
        }
      }
    }
  },
  async created() {
    try {
      const [cats, locs, levels] = await Promise.all([
        AssetCategory.search({ _limit: -1, isDelete: false }),
        FacilityLocation.search({ _limit: -1, isDelete: false }),
        AcademicLevel.search({ _limit: -1 })
      ])

      this.locations = locs || []
      this.academicLevels = levels || []

      // 1. Xây dựng cây danh mục phân cấp
      const itemMap = new Map()
      const childrenMap = new Map()
      const rootItems = []

      ;(cats || []).forEach(item => {
        const idStr = String(item.id || item._id)
        itemMap.set(idStr, item)
        childrenMap.set(idStr, [])
      })

      ;(cats || []).forEach(item => {
        const rawParent = item.parent
        const parentId = rawParent && (rawParent.id || rawParent._id || rawParent)
        const parentIdStr = parentId ? String(parentId) : null
        if (parentIdStr && itemMap.has(parentIdStr)) {
          childrenMap.get(parentIdStr).push(item)
        } else {
          rootItems.push(item)
        }
      })

      const treeOptions = []

      const traverse = (item, depth = 0) => {
        const prefix = depth === 0 ? '' : '— '.repeat(depth) + ' '
        const idStr = String(item.id || item._id)
        treeOptions.push({
          id: item.id,
          name: item.name,
          code: item.code,
          units: item.units,
          depth
        })
        const children = childrenMap.get(idStr) || []
        children.forEach(child => traverse(child, depth + 1))
      }

      rootItems.forEach(root => traverse(root, 0))
      this.categories = treeOptions

      // 2. Xây dựng cây vị trí phân cấp: Tòa nhà -> Tầng -> Phòng học / Kho
      const locMap = new Map()
      const locChildrenMap = new Map()
      const rootLocs = []

      ;(locs || []).forEach(item => {
        const idStr = String(item.id || item._id)
        locMap.set(idStr, item)
        locChildrenMap.set(idStr, [])
      })

      ;(locs || []).forEach(item => {
        const rawParent = item.parent
        const parentId = rawParent && (rawParent.id || rawParent._id || rawParent)
        const parentIdStr = parentId ? String(parentId) : null
        if (parentIdStr && locMap.has(parentIdStr)) {
          locChildrenMap.get(parentIdStr).push(item)
        } else {
          rootLocs.push(item)
        }
      })

      const locTreeOptions = []

      const traverseLoc = (item, depth = 0) => {
        const idStr = String(item.id || item._id)
        locTreeOptions.push({
          id: item.id,
          name: item.name,
          code: item.code,
          type: item.type,
          depth
        })
        const children = locChildrenMap.get(idStr) || []
        children.forEach(child => traverseLoc(child, depth + 1))
      }

      rootLocs.forEach(root => traverseLoc(root, 0))
      this.locations = locTreeOptions
    } catch (e) {
      console.error(e)
    }
  },
  methods: {
    addUnitConversion() {
      this.formData.unitConversions.push({
        unitName: '',
        conversionRate: 1
      })
    },
    removeUnitConversion(index) {
      this.formData.unitConversions.splice(index, 1)
    },
    onCategoryChange(catId) {
      const selectedCat = this.categories.find(c => c.id === catId)
      if (selectedCat && Array.isArray(selectedCat.units) && selectedCat.units.length) {
        this.suggestedUnits = [...selectedCat.units]
        if (!this.formData.id && !this.formData.unit) {
          this.formData.unit = selectedCat.units[0]
        }
      } else {
        this.suggestedUnits = ['Cái', 'Chiếc', 'Bộ', 'Hộp', 'Thùng', 'Lít', 'Kg', 'Ram', 'Cuộn', 'Mét', 'Bình', 'Chai', 'Gói', 'Quyển', 'Cuốn']
      }
      if (!this.formData.id && !this.isManualCode) {
        this.fetchAutoCode()
      }
    },
    onUnitChange() {
      if (!this.formData.id && !this.isManualCode) {
        this.fetchAutoCode()
      }
    },
    onNameInput(val) {
      if (!this.formData.id && !this.isManualCode && !this.formData.category) {
        this.debounceGenerateCode(val)
      }
    },
    onCodeManualInput() {
      if (this.formData.code && this.formData.code.trim()) {
        this.isManualCode = true
      } else {
        this.isManualCode = false
      }
    },
    async fetchAutoCode() {
      try {
        const selectedCat = this.categories.find(c => c.id === this.formData.category)
        const params = {
          category: this.formData.category || undefined,
          categoryCode: (selectedCat && selectedCat.code) || undefined,
          name: (this.formData.name || '').trim() || undefined,
          unit: (this.formData.unit || '').trim() || undefined
        }
        const res = await Asset.generateCode(params)
        if (res && res.code && !this.isManualCode) {
          this.formData.code = res.code
        }
      } catch (e) {
        console.error(e)
      }
    },
    debounceGenerateCode(name) {
      clearTimeout(this.timerCode)
      if (!name || !name.trim()) {
        this.formData.code = ''
        return
      }
      this.timerCode = setTimeout(async () => {
        try {
          const selectedCat = this.categories.find(c => c.id === this.formData.category)
          const params = {
            category: this.formData.category || undefined,
            categoryCode: (selectedCat && selectedCat.code) || undefined,
            name: (name || '').trim() || undefined,
            unit: (this.formData.unit || '').trim() || undefined
          }
          const res = await Asset.generateCode(params)
          if (res && res.code && !this.isManualCode) {
            this.formData.code = res.code
          }
        } catch (e) {
          console.error(e)
        }
      }, 300)
    },
    async getAutoCode() {
      try {
        const selectedCat = this.categories.find(c => c.id === this.formData.category)
        const params = {
          category: this.formData.category || undefined,
          categoryCode: (selectedCat && selectedCat.code) || undefined,
          name: (this.formData.name || '').trim() || undefined,
          unit: (this.formData.unit || '').trim() || undefined
        }
        const res = await Asset.generateCode(params)
        if (res && res.code) {
          this.formData.code = res.code
          this.isManualCode = false
        }
      } catch (e) {
        console.error(e)
      }
    },
    validate() {
      return this.$refs.form.validate()
    },
    getData() {
      return { ...this.formData }
    },
    resetValidation() {
      if (this.$refs.form) this.$refs.form.resetValidation()
    }
  }
}
</script>
